import axios from 'axios';
import { PermissionsUserV3, type ApiPermissionsUsersV3Response } from '@/types/permissionsUsers.v3';
import type { ApiPermissionGrantV3, ApiUserPermissionGrantsV3Response } from '@/types/permissionGrants.v3';
import type { ApiMeV3 } from '@/types/users.v3';
import { waitForDevMocks } from '@/plugins/axios';

/**
 * Org-scoped, fetch-all users list for the granular-permissions admin surface.
 * Calls `GET /v3/users`.
 */
export async function getUserPermissionsListV3(): Promise<PermissionsUserV3[]> {
  await waitForDevMocks();
  const { data } = await axios.get<ApiPermissionsUsersV3Response>('/v3/users');

  return (data.data ?? [])
    .map((user) => new PermissionsUserV3(user))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
}

/**
 * Current user's v3 identity and grant list (`GET /v3/users/me`).
 * Fetched on its own query key, alongside legacy `/me`.
 */
export async function getMeV3(): Promise<ApiMeV3> {
  await waitForDevMocks();
  const { data } = await axios.get<ApiMeV3>('/v3/users/me');

  return {
    ...data,
    permission_grants: data.permission_grants ?? [],
  };
}

/**
 * Saved grants for one user, used to fill the single-user edit drawer.
 * Calls `GET /v3/users/{userTwrn}/permission-grants`.
 */
export async function getUserPermissionGrantsV3(userTwrn: string): Promise<ApiPermissionGrantV3[]> {
  await waitForDevMocks();
  const { data } = await axios.get<ApiUserPermissionGrantsV3Response>(`/v3/users/${userTwrn}/permission-grants`);

  return data.permission_grants ?? [];
}
